<template>
  <div>
    <!-- Toolbar: Copy + Filters (Full Width) -->
    <div class="row text-right mb-3">
      <div class="col">
        <div class="d-flex justify-content-end">
            <b-button class="my-0 mx-3" @click="copyHtmlCode">Copy HTML</b-button>
            <!-- Language Selector -->
            <b-form-group
                label="language"
                v-slot="{ ariaDescribedby }"
                class="m-0 ml-3"
            >
              <b-form-select
                  :options="languageOptions"
                  v-model="selectedLanguage"
                  @change="onFilterChange"
              />
            </b-form-group>

            <!-- Jobboard Selector -->
            <b-form-group label="jobboard" class="m-0 ml-3">
              <b-form-select
                  v-model="selectedJobboard"
                  :options="jobboardOptions"
                  @change="onFilterChange"
              />
            </b-form-group>

            <!-- Region Selector -->
            <b-form-group label="region" class="m-0 ml-3">
              <b-form-select
                  :options="regionOptions"
                  v-model="selectedRegion"
                  @change="onFilterChange"
              />
            </b-form-group>

            <b-form-group label="Companies" class="m-0 ml-3">
              <b-form-select
                  :options="maxCompaniesOptions"
                  v-model="selectedMaxCompanies"
                  @change="onFilterChange"
              />
            </b-form-group>

            <b-form-group label="Offers per company" class="m-0 ml-3">
              <b-form-select
                  :options="maxOfferPerCompanyOptions"
                  v-model="selectedMaxOfferPerCompany"
                  @change="onFilterChange"
              />
            </b-form-group>

            <b-form-group label="Agencies" class="m-0 ml-3">
              <b-form-select
                  :options="agencyModeOptions"
                  v-model="selectedAgencyMode"
                  @change="onFilterChange"
              />
            </b-form-group>
        </div>
      </div>
    </div>

    <!-- Content Section: 1/3 textarea, 2/3 iframe -->
    <div v-if="loading">Loading...</div>
    <div v-else class="row">
      <div class="col-4">
        <textarea v-model="htmlCode" ref="htmlCodeTextarea" />
      </div>
      <div class="col-8">
        <iframe :srcdoc="previewHtml" />
      </div>
    </div>
  </div>
</template>

<script>
import { BFormSelect, BButton, BFormGroup } from "bootstrap-vue";

export default {
  components: { BFormSelect, BButton, BFormGroup },

  data() {
    return {
      loading: false,
      htmlCode: "Html code will be displayed here.",
      previewHtml: "",
      offerAttributes: {},
      jobboards: [],
      selectedJobboard: { name: "lemarche", category: null },
      selectedLanguage: "fr",
      selectedRegion: null,
      languageOptions: [
        { text: "FR", value: "fr" },
        { text: "EN", value: "en" }
      ],
      regionOptions: [
        { text: "All Regions", value: null },
        { text: "Quebec (QC)", value: "QC" },
        { text: "Ontario (ON)", value: "ON" },
        { text: "British Columbia (BC)", value: "BC" },
        { text: "Alberta (AB)", value: "AB" },
        { text: "Manitoba (MB)", value: "MB" },
        { text: "Saskatchewan (SK)", value: "SK" },
        { text: "Nova Scotia (NS)", value: "NS" },
        { text: "New Brunswick (NB)", value: "NB" },
        { text: "Newfoundland and Labrador (NL)", value: "NL" },
        { text: "Prince Edward Island (PE)", value: "PE" },
        { text: "Northwest Territories (NT)", value: "NT" },
        { text: "Yukon (YT)", value: "YT" },
        { text: "Nunavut (NU)", value: "NU" }
      ],
      maxCompaniesOptions: Array.from({ length: 20 }, (_, i) => ({
        value: i + 1,
        text: `${(i + 1)}`
      })),
      selectedMaxCompanies: 12,
      // Max 10 offers per company (precomputed table stores max 10 offers)
      maxOfferPerCompanyOptions: Array.from({ length: 10 }, (_, i) => ({
        value: i + 1,
        text: `${(i + 1)}`
      })),
      selectedMaxOfferPerCompany: 5,
      agencyModeOptions: [
        { value: null, text: "End of list" },
        { value: "hide", text: "Hide" }
      ],
      selectedAgencyMode: null
    };
  },

  computed: {
    jobboardOptions() {
      // ES-1546: the hidden jobsmedia_io_* boards are the old per-subject boards; their
      // subjects are now categories of the main jobsmedia_io board (below), so listing
      // both showed every subject twice.
      const internalJobboards = this.jobboards
          .filter(jb => jb.internal && jb.name !== "career" && !(jb.is_jobsmedia_io && jb.hidden))
          .map(jb => ({
            value: { name: jb.name, category: null },
            text: jb.label
          }));

      // No board is named "jobsmedia" (prod, 2026-10-05): the category filter runs on jobsmedia_io.
      const jobsmediaOptions =
          this.offerAttributes.category?.map(category => ({
            value: { name: "jobsmedia_io", category: category.id },
            text: `JobsMedia.io - ${category.label.en}`
          })) || [];

      return [...internalJobboards, ...jobsmediaOptions];
    }
  },

  async created() {
    this.loading = true;
    await Promise.all([this.loadOfferAttributes(), this.loadJobboards()]);
    await this.loadTemplate();
    this.loading = false;
  },

  methods: {
    async loadOfferAttributes() {
      const { data } = await this.$http.get("/offer-attributes");
      this.offerAttributes = data;
    },

    async loadJobboards() {
      const { data } = await this.$http.get("/jobboards");
      this.jobboards = data;
      this.selectedJobboard = this.jobboardOptions[0]?.value || {};
    },

    async loadTemplate() {
      this.loading = true;

      const jb = this.selectedJobboard;
      const params = new URLSearchParams({
        lang: this.selectedLanguage,
        max_companies: this.selectedMaxCompanies,
        max_offer_per_company: this.selectedMaxOfferPerCompany
      });

      if (jb.category) {
        params.append("category", jb.category);
      }

      if (this.selectedRegion) {
        params.append("region", this.selectedRegion);
      }

      if (this.selectedAgencyMode) {
        params.append("agencies", this.selectedAgencyMode);
      }

      const { data } = await this.$http.get(
          `/enroll_admin/emailing/${jb.name}/newsletter-email-template?${params.toString()}`
      );

      this.htmlCode = data;

      // Full preview (index view) fetched WITH the auth token, then injected
      // into the iframe via srcdoc. An <iframe src> pointing at the API route
      // would be a token-less browser request against an auth:enroll_admin
      // route -> 401 -> blank preview.
      params.append("preview", 1);
      const { data: preview } = await this.$http.get(
          `/enroll_admin/emailing/${jb.name}/newsletter-email-template?${params.toString()}`
      );
      this.previewHtml = preview;

      this.loading = false;
    },

    async copyHtmlCode() {
      try {
        await navigator.clipboard.writeText(this.htmlCode);
        alert("HTML code copied!");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Copy failed:", err);
      }
    },

    // Single unified handler for all filter changes
    onFilterChange() {
      this.loadTemplate();
    }
  }
};
</script>

<style>
textarea,
iframe {
  width: 100%;
  min-height: 450px;
}
select {
  max-width: 250px;
}
</style>